import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import type { CatalogueEntry } from "./documentQueries";
import { notificationsKey, unreadChatKey } from "./portalQueries";

/**
 * What outlived the overview's list of summaries.
 *
 * Until build round 4 (2026-09-27) this file held the arithmetic of the six summary rows plan.md
 * section 5.1 listed then. The overview is a dashboard now and its arithmetic is in `dashboard.ts`.
 * What stays here is the unread chat count, which moved to Chatt in the navigation (`AppShell`), the
 * event types Notiser reads for its security rows, and the document window, which Dokument uses to
 * mark what is new.
 */

/**
 * The unread count's key, under `unreadChatKey` since 2026-09-27.
 *
 * It was `["portal", "chat-unread-summary", tenant]`, and `useMarkChatRead` invalidates
 * `["portal", "chat-unread", tenant]`, which is not a prefix of it. So reading the chat never
 * refreshed the count. On the overview that went unnoticed, because the overview refetched on its own
 * mount; as a count on Chatt in the navigation, which stays mounted, it would have kept saying "1
 * oläst" after the message was read.
 */
export const unreadChatSummaryKey = (tenantId: string, userId: string) =>
  [...unreadChatKey(tenantId), "summary", userId] as const;

/**
 * How many messages in the tenant thread this person has not read.
 *
 * `public.v_unread_chat_count` **inner joins** `public.chat_read_positions`, so a person who has
 * never marked the thread read has no row in the view at all. That state is the ordinary one for a
 * brand new client and it stays until `public.mark_chat_read` runs for the first time, which is what
 * opening `/chat` does.
 *
 * Until 2026-09-22 the missing row was reported as "unknown". It changed because the absence of a read
 * position is not the server declining to answer, it is the server saying this person has read
 * nothing. Nothing read is countable. It is every message in the thread that somebody else sent, so
 * that is what the hook counts when the view has no row, and the count is a real number rather than
 * an invented zero.
 */
export interface UnreadChatSummary {
  unreadCount: number;
}

/**
 * The unread count, asked once for the tenant thread, and counted directly when there is no position.
 *
 * `maybeSingle()` because there is exactly one row to find: `public.chat_threads` holds one thread
 * per tenant, and `chat_read_positions_select_own` narrows the join to this caller's own position.
 * Zero rows resolve as `{ data: null, error: null }`, which is the no-position case below; a failure
 * throws.
 *
 * The explicit `tenant_id` filter is sent even though RLS already scopes the read: a person can hold
 * memberships in more than one tenant and the policy alone would return all of them, which would turn
 * `maybeSingle` into an error about a perfectly legitimate account.
 *
 * **No position, 2026-09-22.** A head count over `public.chat_messages` for the tenant, of every
 * message not sent by this person. A message whose sender has since been deleted has a null
 * `sender_user_id`, and `<>` in SQL is null for it rather than true, so a bare `neq` would quietly
 * drop it from the count; the `or` keeps it. The user id is the one `usePortalIdentity` already
 * resolved through `public.current_user_id()`, never an input.
 *
 * Either read failing throws, so a count that could not be made is a failure and never a zero.
 */
export function useUnreadChatSummary(
  tenantId: string | null,
  userId: string | null
): UseQueryResult<UnreadChatSummary> {
  return useQuery({
    queryKey: unreadChatSummaryKey(tenantId ?? "none", userId ?? "none"),
    enabled: Boolean(tenantId) && Boolean(userId),
    queryFn: async () => {
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("v_unread_chat_count")
        .select("tenant_id, thread_id, unread_count")
        .eq("tenant_id", tenantId as string)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (data && typeof data.unread_count === "number") {
        return { unreadCount: data.unread_count } satisfies UnreadChatSummary;
      }

      const { count, error: countError } = await supabase
        .from("chat_messages")
        .select("id", { count: "exact", head: true })
        .eq("tenant_id", tenantId as string)
        .or(`sender_user_id.is.null,sender_user_id.neq.${userId as string}`);

      if (countError) {
        throw countError;
      }
      if (typeof count !== "number") {
        // An exact count was asked for and none came back. That is a failed read, not zero.
        throw new Error("chat_messages count missing from the response");
      }

      return { unreadCount: count } satisfies UnreadChatSummary;
    }
  });
}

/**
 * `event_type` for the security rows a screen is showing, by notification id. Added 2026-09-22 for the
 * overview's attention rows; Notiser uses it since, and the overview no longer does.
 *
 * A `security` row's destination depends on which security event it is: a new device leads to the
 * sessions, a cancelled agreement to the management section. `useNotifications` does not select
 * `event_type`, so this asks for the one column, for the rows shown, and only when one of them is a
 * security row. `notifications` is recipient-only by policy, so this reads nothing the list did not
 * already hold. The key sits under `notificationsKey`, so every invalidation of the list refreshes it.
 */
export function useAttentionEventTypes(ids: string[]): UseQueryResult<Map<string, string>> {
  return useQuery({
    queryKey: [...notificationsKey, "overview-event-types", ...ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("notifications")
        .select("id, event_type")
        .in("id", ids);

      if (error) {
        throw error;
      }

      return new Map((data ?? []).map((row) => [row.id, row.event_type]));
    }
  });
}

/* --- Dokument ---------------------------------------------------------------------------------- */

/**
 * What "new" means under Dokument, stated as a number because the schema has no other answer.
 *
 * `public.client_document_catalogue` carries `visible_from`, `frozen_at` and `is_current`, and no
 * per-person read state of any kind, so "new" is a window rather than an unread state, and the window
 * is thirty days.
 */
export const NEW_DOCUMENT_DAYS = 30;

export interface DocumentSummary {
  /** Current entries whose `visible_from` falls inside the window. */
  recent: CatalogueEntry[];
  /** Every current entry, so an empty shelf and a quiet month are not rendered alike. */
  currentTotal: number;
}

export function summariseDocuments(
  entries: CatalogueEntry[],
  now: Date = new Date()
): DocumentSummary {
  const cutoff = now.getTime() - NEW_DOCUMENT_DAYS * 24 * 60 * 60 * 1000;

  // `is_current` only. A superseded version becoming visible again is not a new document.
  const current = entries.filter((entry) => entry.is_current);

  return {
    recent: current.filter((entry) => {
      const visible = new Date(entry.visible_from).getTime();
      // An unparseable date is not recent. It is the one value here that could silently promote a
      // row into a list the client is being asked to look at.
      return Number.isNaN(visible) ? false : visible >= cutoff;
    }),
    currentTotal: current.length
  };
}
